import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  useTheme,
  IconButton,
  Avatar,
  Checkbox,
  ListItemText,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableSortLabel from '@mui/material/TableSortLabel';
import Star from '@mui/icons-material/Star';
import StarBorder from '@mui/icons-material/StarBorder';
import { School } from '../../types';
import { usePrograms } from '../../contexts/ProgramsContext';
import { useSchools } from '../../contexts/SchoolsContext';
import { useEvents } from '../../contexts/EventsContext';

import { ref as dbRef, get as dbGet } from 'firebase/database';
import { database, auth } from '../../firebase';
import { useAuthState } from '../../hooks/useAuthState';

interface SchoolListProps {
  schools: School[];
  useStateFilter?: boolean; // If true, shows "State" instead of "Province"
}

const SchoolList: React.FC<SchoolListProps> = ({ schools, useStateFilter = false }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();

  const { programs } = usePrograms();
  const { events } = useEvents();
  const { user, isAuthStateStable } = useAuthState();

  
  // Helper function to get filter from URL or localStorage
  const getFilterFromURL = (key: string, defaultValue: any) => {
    const urlValue = searchParams.get(key);
    if (urlValue !== null) {
      if (key === 'province' || key === 'type') {
        return urlValue.split(',').filter(Boolean);
      }
      return urlValue;
    }
    
    // Fallback to localStorage
    const storedValue = localStorage.getItem(`schoolsList_${key}`);
    if (storedValue) {
      if (key === 'province' || key === 'type') {
        return JSON.parse(storedValue);
      }
      return storedValue;
    }
    return defaultValue;
  };

  // State for filters with URL persistence
  const [searchQuery, setSearchQuery] = useState(() => getFilterFromURL('search', ''));
  const [provinceFilter, setProvinceFilter] = useState<string[]>(() => getFilterFromURL('province', []));
  const [typeFilter, setTypeFilter] = useState<string[]>(() => getFilterFromURL('type', []));

  // Wrapper functions to update both state and URL/localStorage
  const updateSearchQuery = (value: string) => {
    setSearchQuery(value);
    const newSearchParams = new URLSearchParams(searchParams);
    if (value === '') {
      newSearchParams.delete('search');
    } else {
      newSearchParams.set('search', value);
    }
    setSearchParams(newSearchParams);
    localStorage.setItem('schoolsList_search', value);
  };

  const updateProvinceFilter = (value: string[]) => {
    setProvinceFilter(value);
    const newSearchParams = new URLSearchParams(searchParams);
    if (value.length === 0) {
      newSearchParams.delete('province');
    } else {
      newSearchParams.set('province', value.join(','));
    }
    setSearchParams(newSearchParams);
    localStorage.setItem('schoolsList_province', JSON.stringify(value));
  };

  const updateTypeFilter = (value: string[]) => {
    setTypeFilter(value);
    const newSearchParams = new URLSearchParams(searchParams);
    if (value.length === 0) {
      newSearchParams.delete('type');
    } else {
      newSearchParams.set('type', value.join(','));
    }
    setSearchParams(newSearchParams);
    localStorage.setItem('schoolsList_type', JSON.stringify(value));
  };

  // Initialize viewMode with localStorage preference, defaulting to 'card'
  const [viewMode, setViewMode] = useState<'card' | 'list'>(() => {
    const savedViewMode = localStorage.getItem('schoolsViewMode');
    return (savedViewMode === 'card' || savedViewMode === 'list') ? savedViewMode : 'card';
  });
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'school', direction: 'asc' });


  
  // State for managing favorites (read-only display)
  const [favoriteSchools, setFavoriteSchools] = useState<Set<string>>(new Set());

  // Feed icons are now loaded upfront with schools, no need to load separately

  // Sync filter state with URL parameters when component mounts or URL changes
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    const urlProvince = searchParams.get('province');
    const urlType = searchParams.get('type');

    if (urlSearch !== null && urlSearch !== searchQuery) {
      setSearchQuery(urlSearch);
    }
    if (urlProvince !== null) {
      const provinceArray = urlProvince.split(',').filter(Boolean);
      if (JSON.stringify(provinceArray) !== JSON.stringify(provinceFilter)) {
        setProvinceFilter(provinceArray);
      }
    }
    if (urlType !== null) {
      const typeArray = urlType.split(',').filter(Boolean);
      if (JSON.stringify(typeArray) !== JSON.stringify(typeFilter)) {
        setTypeFilter(typeArray);
      }
    }
  }, [searchParams, searchQuery, provinceFilter, typeFilter]);

  // Get unique provinces and types for filters
  const provinces = useMemo(() => 
    Array.from(new Set(schools.map(school => school.province))).sort(),
    [schools]
  );

  const schoolTypes = useMemo(() => 
    Array.from(new Set(schools.map(school => school.type))).sort(),
    [schools]
  );

  // Filter schools based on search and filters
  const filteredSchools = useMemo(() => {
    return schools.filter(school => {
      const matchesSearch = 
        school.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        school.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
        school.province.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesProvince = provinceFilter.length > 0 ? provinceFilter.includes(school.province) : true;
      const matchesType = typeFilter.length > 0 ? typeFilter.includes(school.type) : true;
      
      return matchesSearch && matchesProvince && matchesType;
    });
  }, [schools, searchQuery, provinceFilter, typeFilter]);

  const handleProvinceChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const provinceArray = typeof value === 'string' ? value.split(',') : value;
    updateProvinceFilter(provinceArray);
  };

  const handleTypeChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const typeArray = typeof value === 'string' ? value.split(',') : value;
    updateTypeFilter(typeArray);
  };

  const handleSort = (columnKey: string) => {
    setSortConfig((prev) => {
      if (prev.key === columnKey) {
        return { key: columnKey, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key: columnKey, direction: 'asc' };
    });
  };

  const sortedSchools = useMemo(() => {
    const schoolsCopy = [...filteredSchools];
    if (viewMode === 'card') {
      schoolsCopy.sort((a, b) => a.name.localeCompare(b.name));
      return schoolsCopy;
    }
    schoolsCopy.sort((a, b) => {
      let aValue: any;
      let bValue: any;
      switch (sortConfig.key) {
        case 'school':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'city':
          aValue = a.city.toLowerCase();
          bValue = b.city.toLowerCase();
          break;
        case 'province':
          aValue = a.province.toLowerCase();
          bValue = b.province.toLowerCase();
          break;

        default:
          aValue = '';
          bValue = '';
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return schoolsCopy;
  }, [filteredSchools, sortConfig, viewMode]);



  const handleSchoolClick = (schoolId: string) => {
    // Preserve current filter state in URL when navigating to school details
    const currentParams = new URLSearchParams(searchParams);
    const basePath = useStateFilter ? '/schools-us' : '/schools';
    navigate(`${basePath}/${schoolId}?${currentParams.toString()}`);
  };

  const handleEventsChipClick = (event: React.MouseEvent, schoolId: string) => {
    event.stopPropagation(); // Prevent card click
    const basePath = useStateFilter ? '/schools-us' : '/schools';
    navigate(`${basePath}/${schoolId}?tab=events`);
  };

  // Load user's favorite schools when auth state is stable and schools are loaded
  useEffect(() => {
    const loadFavorites = async () => {
      try {
        const currentUser = auth.currentUser;
        
        // Clear favorites if no user or auth state not stable
        if (!currentUser || !isAuthStateStable) {
          console.log('🔄 SchoolList: Clearing favorites - no user or auth state not stable');
          setFavoriteSchools(new Set());
          return;
        }

        console.log('🔄 SchoolList: Loading favorites for user:', currentUser.uid);

        const favoritesPromises = schools.map(async (school) => {
          const favoriteRef = dbRef(database, `favoriteSchoolProgram/${school.name}/${school.name}/${currentUser.uid}`);
          const snapshot = await dbGet(favoriteRef);
          return { schoolName: school.name, isFavorite: snapshot.exists() };
        });

        const results = await Promise.all(favoritesPromises);
        const favorites = new Set(
          results.filter(result => result.isFavorite).map(result => result.schoolName)
        );
        setFavoriteSchools(favorites);
        console.log('✅ SchoolList: Loaded favorites:', Array.from(favorites));
      } catch (error) {
        console.error('Error loading favorites:', error);
        setFavoriteSchools(new Set());
      }
    };

    if (schools.length > 0) {
      loadFavorites();
    }
  }, [schools, user, isAuthStateStable]);



  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <Box sx={{ 
        width: '100%',
        px: 0.5,
        display: 'flex',
        flexDirection: 'column',
        height: '100vh'
      }}>
      {/* Debug: Print schoolID, name, and feedIcon for Toronto Metropolitan University */}
      {schools.filter(s => s.name.toLowerCase().includes('toronto metropolitan')).map(s => {
        // @ts-ignore
        // eslint-disable-next-line no-console
        console.log('DEBUG TorontoMet:', { schoolID: s.id, name: s.name, feedIcon: s.feedIcon });
        return null;
      })}
      
      {/* Sticky Search and Filter Section */}
      <Box sx={{ 
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        backgroundColor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        py: 2,
        mb: 2
      }}>
        <Box sx={{ 
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2
        }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search schools..."
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flex: { md: 2 } }}
        />
        <FormControl fullWidth sx={{ flex: { md: 1 } }}>
          <InputLabel>{useStateFilter ? 'State' : 'Province'}</InputLabel>
          <Select
            multiple
            value={provinceFilter}
            label={useStateFilter ? 'State' : 'Province'}
            onChange={handleProvinceChange}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return useStateFilter ? 'All States' : 'All Provinces';
              }
              if (selected.length === 1) {
                return selected[0];
              }
              return useStateFilter ? `${selected.length} states selected` : `${selected.length} provinces selected`;
            }}
          >
            {provinces.map((province) => (
              <MenuItem key={province} value={province}>
                <Checkbox checked={provinceFilter.includes(province)} />
                <ListItemText primary={province} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth sx={{ flex: { md: 1 } }}>
          <InputLabel>Type</InputLabel>
          <Select
            multiple
            value={typeFilter}
            label="Type"
            onChange={handleTypeChange}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return 'All Types';
              }
              if (selected.length === 1) {
                return selected[0];
              }
              return `${selected.length} types selected`;
            }}
          >
            {schoolTypes.map((type) => (
              <MenuItem key={type} value={type}>
                <Checkbox checked={typeFilter.includes(type)} />
                <ListItemText primary={type} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        </Box>
      </Box>

      {/* Scrollable Content Area */}
      <Box sx={{ 
        flex: 1,
        overflowY: 'auto',
        px: 0.5
      }}>
        {/* Results Count and View Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
        <Typography variant="subtitle1">
          {filteredSchools.length} schools displayed
      </Typography>
        <Box>
          <IconButton
            aria-label="Card view"
            color={viewMode === 'card' ? 'primary' : 'default'}
            onClick={() => {
              setViewMode('card');
              localStorage.setItem('schoolsViewMode', 'card');
            }}
            size="large"
          >
            <ViewModuleIcon />
          </IconButton>
          <IconButton
            aria-label="List view"
            color={viewMode === 'list' ? 'primary' : 'default'}
            onClick={() => {
              setViewMode('list');
              localStorage.setItem('schoolsViewMode', 'list');
            }}
            size="large"
          >
            <ViewListIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Schools Display */}
      {viewMode === 'card' ? (
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)'
        },
        gap: 2
      }}>
          {sortedSchools.map((school) => {
          const programCount = programs?.filter(p => p.schoolName === school.name).length || 0;
          
          // Filter events to only count current and future events (same logic as SchoolProfile)
          const now = new Date();
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Start of today
          const eventCount = events?.filter(e => {
            // Filter by school (using hostName to match SchoolProfile logic)
            if ((e as any).hostName !== school.name) return false;
            
            // Filter by date - only current and future events
            const eventDate = new Date(((e as any).startTime || '').replace(' ', 'T'));
            return !isNaN(eventDate.getTime()) && eventDate >= today;
          }).length || 0;
          return (
            <Card 
              key={school.id}
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[4],
                  cursor: 'pointer',
                },
              }}
              onClick={() => handleSchoolClick(school.id)}
            >
              <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mb: 2, justifyContent: 'flex-start' }}>
                    <Avatar
                      sx={{ width: 45, height: 45, bgcolor: 'grey.200', color: 'grey.600', fontWeight: 600, mr: 1 }}
                    >
                      {(school.feedIcon || school.resultsImage) ? (
                        (() => {
                          if (school.name.toLowerCase().includes('toronto metropolitan')) {
                            // eslint-disable-next-line no-console
                            console.log('DEBUG Avatar img src:', school.feedIcon || school.resultsImage);
                          }
                          return (
                            <img
                              src={school.feedIcon || school.resultsImage}
                              alt={school.name}
                              style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%', border: '0.5px solid black' }}
                            />
                          );
                        })()
                      ) : (
                        school.name ? school.name[0] : ''
                      )}
                    </Avatar>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', flex: 1, justifyContent: 'space-between' }}>
                      <Typography 
                        variant="h6" 
                        component="div" 
                        sx={{ 
                          flex: 1,
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          hyphens: 'none',
                          minWidth: 0,
                          pr: 1
                        }}
                      >
                        {school.name}
                      </Typography>
                      <Box sx={{ 
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: favoriteSchools.has(school.name) ? '#8dd439' : 'grey.400',
                        flexShrink: 0
                      }}>
                        {favoriteSchools.has(school.name) ? (
                          <Star sx={{ fontSize: '36px' }} />
                        ) : (
                          <StarBorder sx={{ fontSize: '36px' }} />
                        )}
                      </Box>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LocationOnIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      {school.city}, {school.province}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {school.type}
                  </Typography>
                </Box>



                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {school.description}
                </Typography>

                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Tooltip title="Number of programs">
                    <Chip 
                      label={`${programCount} Programs`}
                      size="small"
                      variant="outlined"
                    />
                  </Tooltip>
                  <Tooltip title="Number of events">
                    <Chip 
                      label={`${eventCount} Events`}
                      size="small"
                      variant="outlined"
                      clickable
                      onClick={(event) => handleEventsChipClick(event, school.id)}
                    />
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
      ) : (
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
            <Box component="thead">
              <Box component="tr">
                <Box component="th" sx={{ p: 1, width: 50, textAlign: 'center', verticalAlign: 'bottom' }}>
                  {/* Star column */}
                </Box>
                <Box component="th" sx={{ p: 1, width: 56 }}></Box>
                <Box component="th" sx={{ p: 1, minWidth: 220, maxWidth: 320, textAlign: 'left', verticalAlign: 'bottom', cursor: 'pointer' }} onClick={() => handleSort('school')}>
                  <TableSortLabel
                    active={sortConfig.key === 'school'}
                    direction={sortConfig.key === 'school' ? sortConfig.direction : 'asc'}
                  >
                    School
                  </TableSortLabel>
                </Box>
                <Box component="th" sx={{ p: 1, minWidth: 160, maxWidth: 220, textAlign: 'left', verticalAlign: 'bottom', cursor: 'pointer' }} onClick={() => handleSort('city')}>
                  <TableSortLabel
                    active={sortConfig.key === 'city'}
                    direction={sortConfig.key === 'city' ? sortConfig.direction : 'asc'}
                  >
                    City
                  </TableSortLabel>
                </Box>
                <Box component="th" sx={{ p: 1, textAlign: 'left', verticalAlign: 'bottom', cursor: 'pointer' }} onClick={() => handleSort('province')}>
                  <TableSortLabel
                    active={sortConfig.key === 'province'}
                    direction={sortConfig.key === 'province' ? sortConfig.direction : 'asc'}
                  >
                    {useStateFilter ? 'State' : 'Province'}
                  </TableSortLabel>
                </Box>

              </Box>
            </Box>
            <Box component="tbody">
              {sortedSchools.map((school) => {
                return (
                  <Box
                    component="tr"
                    key={school.id}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'grey.50' },
                      borderBottom: 1,
                      borderColor: 'divider'
                    }}
                    onClick={() => handleSchoolClick(school.id)}
                  >
                    <Box component="td" sx={{ p: 1, width: 50, textAlign: 'center', verticalAlign: 'top' }}>
                      <Box sx={{ 
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: favoriteSchools.has(school.name) ? '#8dd439' : 'grey.400',
                        p: 0.5
                      }}>
                        {favoriteSchools.has(school.name) ? (
                          <Star sx={{ fontSize: '30px' }} />
                        ) : (
                          <StarBorder sx={{ fontSize: '30px' }} />
                        )}
                      </Box>
                    </Box>
                    <Box component="td" sx={{ p: 1, textAlign: 'center', width: 56 }}>
                      <Avatar
                        sx={{ width: 40, height: 40, bgcolor: 'grey.200', color: 'grey.600', fontWeight: 600 }}
                      >
                        {(school.feedIcon || school.resultsImage) ? (
                          <img
                            src={school.feedIcon || school.resultsImage}
                            alt={school.name}
                            style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%', border: '0.5px solid black' }}
                          />
                        ) : (
                          school.name ? school.name[0] : ''
                        )}
                      </Avatar>
                    </Box>
                    <Box component="td" sx={{ p: 1, minWidth: 220, maxWidth: 320 }}>
                      <Typography component="span">
                        {school.name}
                      </Typography>
                    </Box>
                    <Box component="td" sx={{ p: 1, minWidth: 160, maxWidth: 220 }}>{school.city}</Box>
                    <Box component="td" sx={{ p: 1 }}>{school.province}</Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}

      {/* No Results Message */}
      {filteredSchools.length === 0 && (
        <Box 
          sx={{ 
            textAlign: 'center', 
            py: 4,
            color: 'text.secondary'
          }}
        >
          <Typography variant="h6">
            No schools found matching your criteria
          </Typography>
          <Typography variant="body2">
            Try adjusting your search or filters
          </Typography>
        </Box>
        )}
      </Box>
      </Box>
    </>
  );
};

export default SchoolList;
